import * as React from 'react'
import {ReactElement} from 'react'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../../../../styledComponents/Constants'
import {TopicInfo} from '../../repository/NatGetDisasterTopic'
import {StyledDiv} from '../../../../../styledComponents/StyledTypeDefinitions'
import {Box} from '@mui/material'
import {NatDisasterHelper} from '../../common/NatDisasterHelper'
import {Moment} from 'moment/moment'
import moment from 'moment'
import NatDisasterActionMenu from '../NatDisasterActionMenu'
import {NatResidentConstructionLocationState} from '../../../applications/constructions/NatResidentApplicationInterface'
import EventEmitterExResident from '../../../../../components/shared/pageBaseComponents/EventEmitterExResident'
import {AsyncCallFunctionType} from '../../../../../components/shared/hoc/withAsyncCommunication'
import NatDisasterRepo from '../../repository/NatDisasterRepo'
import {Messages} from '../../../../../Messages'
import NatResidentMessageDialogComponent from '../../../common/resident/NatResidentMessageDialogComponent'
import {DeleteForever} from '@mui/icons-material'
import {currentMedia} from '../../../../../styledComponents/ResponsiveConvenience'
import {judgeAuthorityCheck} from '../../../../common/util/Natutil'
import {AVAILABLE_OR_NOT} from '../../../../../CodeMaster'
import {HKConstants} from '../../../../../HKConstants'
import {Location, NavigateFunction} from 'react-router-dom'

interface Props {
    pastFlag?: boolean,
    condominiumCd: string
    topicInfo: TopicInfo
    currentDateTime: Moment
    navigate: NavigateFunction
    disasterRepo: NatDisasterRepo
    location: Location<NatResidentConstructionLocationState>
    eventEmitter: EventEmitterExResident
    asyncCallFunction: AsyncCallFunctionType
}

interface State {
    isDeleteOpenDialog: boolean
}

export default class NatDisasterTopicArea extends React.PureComponent<Props, State> {
    constructor(props: Readonly<Props>) {
        super(props)
        this.state = {
            isDeleteOpenDialog: false
        }
    }

    handleEditClick = (): void => {
        const {condominiumCd, topicInfo} = this.props
        this.props.navigate(`/resident/condominiums/${condominiumCd}/disasters/topics/${topicInfo.topicId}/edit`)
    }

    handleDeleteClick = (): void => {
        this.setState({isDeleteOpenDialog: true})
    }

    handleCancelClick = (): void => {
        this.setState({isDeleteOpenDialog: false})
    }

    handleDeleteOkClick = async () => {
        const {disasterRepo, eventEmitter, condominiumCd, topicInfo} = this.props

        this.setState({isDeleteOpenDialog: false})
        await this.props.eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                disasterRepo.deleteDisasterTopic.bind(disasterRepo, condominiumCd, topicInfo.topicId),
                (httpErrorOrNetworkError) => {
                    eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                }
            )
            if (response && response.requestError === null) {
                this.props.navigate(`/resident/condominiums/${condominiumCd}/disasters/topics`, {replace: true})
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    renderDeleteDialog(): ReactElement {
        return (
            <NatResidentMessageDialogComponent
                open={this.state.isDeleteOpenDialog}
                params={{
                    title: '削除確認',
                    titleIcon: <DeleteTitleIconWrapper><DeleteForever/></DeleteTitleIconWrapper>,
                    errorLevel: Messages.IOPCC0011.errorLevel,
                    message: Messages.IDISC0001.message,
                    buttonProps: [
                        {
                            isDefault: false,
                            buttonLabel: '削除する',
                            buttonHandler: this.handleDeleteOkClick,
                            color: Colors.Alert
                        },
                        {
                            isDefault: false,
                            buttonLabel: 'キャンセル',
                            buttonHandler: this.handleCancelClick
                        }
                    ]
                }}
            />
        )
    }

    render(): ReactElement {
        const {pastFlag, currentDateTime} = this.props
        const {
            title,
            category,
            description,
            updaterRoleId,
            updateDatetime,
            editAvailability,
            deleteAvailability
        } = this.props.topicInfo

        const isRegisterAuthority =
            judgeAuthorityCheck(HKConstants.CMN.AUTH.REGISTER_TOPIC_DISASTER_INFORMATION_SHARING)
        const isDeletable = isRegisterAuthority && deleteAvailability === AVAILABLE_OR_NOT.AVAILABLE.value
        const isEditable = isRegisterAuthority && editAvailability === AVAILABLE_OR_NOT.AVAILABLE.value

        return (
            <TopicArea>
                {this.renderDeleteDialog()}
                <TopicContainer>
                    <Box width={'100%'} minWidth={'0'}>
                        <TopicHeader>
                            <CategoryLabel>
                                {NatDisasterHelper.getTopicCategoryLabel(category)}
                            </CategoryLabel>
                            <Box pl={'5px'}>
                                {NatDisasterHelper.getTopicCreator(updaterRoleId)}
                            </Box>
                            <Box textAlign={'right'} flexGrow={1}>
                                {NatDisasterHelper.formatDoNotDisplayCurrentYear(
                                    currentDateTime, moment(updateDatetime)
                                )}
                            </Box>
                            <Box ml={'9px'}>
                                {!pastFlag &&
                                    <NatDisasterActionMenu
                                        id={'topicMenuButton'}
                                        label={'トピック'}
                                        isDeletable={isDeletable}
                                        isEditable={isEditable}
                                        onDelete={this.handleDeleteClick}
                                        onEdit={this.handleEditClick}
                                    />
                                }
                            </Box>
                        </TopicHeader>
                        <TopicTitle>{title}</TopicTitle>
                        {description &&
                            <Box mt={'15px'}>
                                <TopicDescription>{description}</TopicDescription>
                            </Box>
                        }
                    </Box>
                </TopicContainer>
            </TopicArea>
        )
    }
}

const TopicArea = styled.div`
    background-color: ${Colors.Surface};
    width: calc(100% + 30px);
    margin: 0 -15px;
`

const TopicContainer = styled.div`
    padding: 15px 15px;
    position: relative;
    display: flex;
    line-height: 18px;
`
const TopicHeader: StyledDiv = styled.div`
    display: flex;
    align-items: center;
    font-size: ${FontSize.Small};
    color: ${Colors.Body};
`

const CategoryLabel = styled.div`
    background-color: ${Colors.Background};
    text-align: center;
    padding: 3px 10px;
`

const TopicTitle: StyledDiv = styled.div`
    margin-top: 5px;
    font-size: ${FontSize.Accent};
    color: ${Colors.Body};
    line-height: 30px;
    font-weight: ${FontWeights.Bold};
`

const TopicDescription: StyledDiv = styled.div`
    font-size: ${FontSize.Middle};
    color: ${Colors.Body};
    line-height: 21px;
    white-space: pre-wrap;
`

const DeleteTitleIconWrapper = styled.div`
    line-height: 1;

    svg {
        color: ${Colors.Alert};
        font-size: ${FontSize.ExtraBig} !important;
        ${currentMedia.lessThan('sp')`
      font-size: ${FontSize.Big}!important;
   `};
    }
`